import { useEffect, useState } from 'react';
import { BookOpen, Info, Loader2, X, FileText, GraduationCap, Layers } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface LearningOutcomes {
  knowledge?: string[];
  skills?: string[];
  competence?: string[];
}

interface ModuleData {
  id: string;
  module_code: string;
  title: string;
  description: string | null;
  ects_credits: number | null;
  estimated_hours: number | null;
  learning_outcomes: LearningOutcomes | null;
  content_modules: unknown[] | null;
  order_index: number;
}

interface PedagogicalContext {
  exam_set: { id: string; title: string; status: string };
  study_plan: {
    id: string;
    title: string;
    description: string | null;
    ects_credits: number | null;
    estimated_hours: number | null;
    target_group: string | null;
    admission_requirements: string | null;
    teaching_learning_methods: string | null;
    assessment_criteria: string | null;
    learning_outcomes: LearningOutcomes | null;
    metadata: Record<string, unknown> | null;
  };
  coverage: {
    covers_full_plan: boolean;
    included_module_codes: string[] | null;
    total_modules_in_plan: number;
    effective_module_count: number;
    effective_ects: number | null;
    effective_estimated_hours: number | null;
    effective_learning_outcomes: LearningOutcomes;
  };
  effective_modules: ModuleData[];
}

interface Props {
  examSetId: string;
}

export function PedagogicalContextDisplay({ examSetId }: Props) {
  const [context, setContext] = useState<PedagogicalContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showFullModal, setShowFullModal] = useState(false);

  useEffect(() => {
    if (!examSetId) {
      setContext(null);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await authenticatedFetch(`/api/exams/${examSetId}/pedagogical-context`);
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || data.message || 'Kunne ikke hente faglig grunnlag');
          setContext(null);
        } else {
          setContext(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Kunne ikke hente faglig grunnlag');
        setContext(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [examSetId]);

  if (!examSetId) return null;

  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border rounded p-3">
        <Loader2 className="h-4 w-4 animate-spin" />
        Henter faglig grunnlag…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
        {error}
      </div>
    );
  }

  if (!context) return null;

  const { study_plan, coverage, effective_modules } = context;
  const isSubset = !coverage.covers_full_plan;
  const totalOutcomes =
    (coverage.effective_learning_outcomes.knowledge?.length || 0) +
    (coverage.effective_learning_outcomes.skills?.length || 0) +
    (coverage.effective_learning_outcomes.competence?.length || 0);

  return (
    <>
      <div className="mt-3 bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="h-4 w-4 text-kf-blue-700 flex-shrink-0" />
              <span className="text-xs font-medium text-kf-blue-900 uppercase tracking-wide">
                Faglig grunnlag for kompetanseattesten
              </span>
              <div
                className="relative inline-flex"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <button
                  type="button"
                  onClick={() => setShowTooltip(s => !s)}
                  className="text-kf-blue-600 hover:text-kf-blue-800"
                  aria-label="Vis detaljer"
                >
                  <Info className="h-4 w-4" />
                </button>
                {showTooltip && (
                  <div className="absolute z-20 left-6 top-0 w-80 bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-xs text-gray-700">
                    <div className="font-semibold text-gray-900 mb-1">{study_plan.title}</div>
                    {study_plan.description && (
                      <p className="mb-2 line-clamp-4">{study_plan.description}</p>
                    )}
                    {isSubset && totalOutcomes > 0 && (
                      <div className="border-t pt-2 mt-2">
                        <div className="font-semibold text-gray-900 mb-1">
                          Læringsutbytter i de valgte modulene ({totalOutcomes})
                        </div>
                        {coverage.effective_learning_outcomes.knowledge?.length ? (
                          <div className="mb-1">
                            <span className="font-medium text-kf-blue-700">Kunnskap:</span>{' '}
                            {coverage.effective_learning_outcomes.knowledge.length} utbytter
                          </div>
                        ) : null}
                        {coverage.effective_learning_outcomes.skills?.length ? (
                          <div className="mb-1">
                            <span className="font-medium text-kf-blue-700">Ferdigheter:</span>{' '}
                            {coverage.effective_learning_outcomes.skills.length} utbytter
                          </div>
                        ) : null}
                        {coverage.effective_learning_outcomes.competence?.length ? (
                          <div>
                            <span className="font-medium text-kf-blue-700">Generell kompetanse:</span>{' '}
                            {coverage.effective_learning_outcomes.competence.length} utbytter
                          </div>
                        ) : null}
                      </div>
                    )}
                    <p className="text-gray-500 italic mt-2 pt-2 border-t">
                      Klikk lenken under for full oversikt.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-900 font-semibold mb-1">{study_plan.title}</div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700 mb-2">
              {isSubset ? (
                <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-900 px-2 py-0.5 rounded">
                  <Layers className="h-3 w-3" />
                  {coverage.effective_module_count} av {coverage.total_modules_in_plan} moduler
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 bg-green-100 text-green-900 px-2 py-0.5 rounded">
                  <Layers className="h-3 w-3" />
                  Hele studieplanen ({coverage.total_modules_in_plan} moduler)
                </span>
              )}
              {coverage.effective_ects && (
                <span className="inline-flex items-center gap-1">
                  <GraduationCap className="h-3 w-3" />
                  {coverage.effective_ects} kompetansepoeng
                </span>
              )}
              {coverage.effective_estimated_hours && (
                <span>≈ {coverage.effective_estimated_hours} timer</span>
              )}
            </div>

            <button
              type="button"
              onClick={() => setShowFullModal(true)}
              className="text-sm text-kf-blue-700 hover:text-kf-blue-900 underline inline-flex items-center gap-1"
            >
              <FileText className="h-3.5 w-3.5" />
              Vis studieplan og inkluderte moduler for dette kurset
            </button>
          </div>
        </div>
      </div>

      {showFullModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="border-b px-6 py-4 flex items-start justify-between gap-3 sticky top-0 bg-white rounded-t-lg">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{study_plan.title}</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Faglig grunnlag for kursinstans (basert på prøvesett: {context.exam_set.title})
                </p>
              </div>
              <button
                onClick={() => setShowFullModal(false)}
                className="text-gray-500 hover:text-gray-900 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 py-5 space-y-5">
              {/* Omfang og dekning */}
              <section>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">Omfang</h4>
                <div className="bg-gray-50 border rounded p-3 text-sm space-y-1">
                  <div>
                    <span className="text-gray-600">Dekning:</span>{' '}
                    {isSubset ? (
                      <strong className="text-amber-800">
                        Et utvalg av {coverage.total_modules_in_plan} moduler — kurset dekker
                        {' '}{coverage.effective_module_count}{' '}
                        {coverage.effective_module_count === 1 ? 'modul' : 'moduler'}
                      </strong>
                    ) : (
                      <strong className="text-green-800">
                        Hele studieplanen — {coverage.total_modules_in_plan} moduler
                      </strong>
                    )}
                  </div>
                  {coverage.effective_ects && (
                    <div>
                      <span className="text-gray-600">Kompetansepoeng:</span>{' '}
                      <strong>{coverage.effective_ects}</strong>
                      {isSubset && study_plan.ects_credits && (
                        <span className="text-gray-500"> (av {study_plan.ects_credits} totalt)</span>
                      )}
                    </div>
                  )}
                  {coverage.effective_estimated_hours && (
                    <div>
                      <span className="text-gray-600">Estimert tid:</span>{' '}
                      <strong>~{coverage.effective_estimated_hours} timer</strong>
                    </div>
                  )}
                </div>
              </section>

              {/* Studieplan-beskrivelse */}
              {study_plan.description && (
                <section>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Beskrivelse</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{study_plan.description}</p>
                </section>
              )}

              {/* Målgruppe + opptakskrav */}
              {(study_plan.target_group || study_plan.admission_requirements) && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {study_plan.target_group && (
                    <section>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Målgruppe</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{study_plan.target_group}</p>
                    </section>
                  )}
                  {study_plan.admission_requirements && (
                    <section>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Opptakskrav</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{study_plan.admission_requirements}</p>
                    </section>
                  )}
                </div>
              )}

              {/* Læringsutbytter */}
              <section>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Læringsutbytter {isSubset
                    ? 'fra de valgte modulene'
                    : effective_modules.length > 0
                      ? 'aggregert fra alle moduler'
                      : 'fra studieplanen'}
                </h4>
                <div className="space-y-3">
                  {coverage.effective_learning_outcomes.knowledge?.length ? (
                    <div>
                      <div className="text-xs font-medium text-kf-blue-700 uppercase tracking-wide mb-1">
                        Kunnskap ({coverage.effective_learning_outcomes.knowledge.length})
                      </div>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-1">
                        {coverage.effective_learning_outcomes.knowledge.map((k, i) => (
                          <li key={i}>{k}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {coverage.effective_learning_outcomes.skills?.length ? (
                    <div>
                      <div className="text-xs font-medium text-kf-blue-700 uppercase tracking-wide mb-1">
                        Ferdigheter ({coverage.effective_learning_outcomes.skills.length})
                      </div>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-1">
                        {coverage.effective_learning_outcomes.skills.map((s, i) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  {coverage.effective_learning_outcomes.competence?.length ? (
                    <div>
                      <div className="text-xs font-medium text-kf-blue-700 uppercase tracking-wide mb-1">
                        Generell kompetanse ({coverage.effective_learning_outcomes.competence.length})
                      </div>
                      <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 ml-1">
                        {coverage.effective_learning_outcomes.competence.map((c, i) => (
                          <li key={i}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </section>

              {/* Moduler i dette kurset — kun vist hvis planen har moduler */}
              {effective_modules.length > 0 ? (
              <section>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                  Moduler i kurset ({effective_modules.length})
                </h4>
                <div className="space-y-2">
                  {effective_modules.map(mod => {
                    const loCount =
                      (mod.learning_outcomes?.knowledge?.length || 0) +
                      (mod.learning_outcomes?.skills?.length || 0) +
                      (mod.learning_outcomes?.competence?.length || 0);
                    return (
                      <details key={mod.id} className="border rounded p-3 bg-white">
                        <summary className="cursor-pointer flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 text-sm">
                              <span className="bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded text-xs font-mono mr-2">
                                {mod.module_code}
                              </span>
                              {mod.title}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {mod.ects_credits ? `${mod.ects_credits} sp` : ''}
                              {mod.ects_credits && mod.estimated_hours ? ' · ' : ''}
                              {mod.estimated_hours ? `${mod.estimated_hours} timer` : ''}
                              {(mod.ects_credits || mod.estimated_hours) && loCount > 0 ? ' · ' : ''}
                              {loCount > 0 ? `${loCount} læringsutbytter` : 'Ingen læringsutbytter'}
                            </div>
                          </div>
                        </summary>
                        <div className="mt-3 pt-3 border-t space-y-3">
                          {mod.description && (
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">{mod.description}</p>
                          )}
                          {mod.learning_outcomes?.knowledge?.length ? (
                            <div>
                              <div className="text-xs font-medium text-kf-blue-700 mb-1">Kunnskap</div>
                              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 ml-1">
                                {mod.learning_outcomes.knowledge.map((k, i) => <li key={i}>{k}</li>)}
                              </ul>
                            </div>
                          ) : null}
                          {mod.learning_outcomes?.skills?.length ? (
                            <div>
                              <div className="text-xs font-medium text-kf-blue-700 mb-1">Ferdigheter</div>
                              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 ml-1">
                                {mod.learning_outcomes.skills.map((s, i) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          ) : null}
                          {mod.learning_outcomes?.competence?.length ? (
                            <div>
                              <div className="text-xs font-medium text-kf-blue-700 mb-1">Generell kompetanse</div>
                              <ul className="list-disc list-inside text-sm text-gray-700 space-y-0.5 ml-1">
                                {mod.learning_outcomes.competence.map((c, i) => <li key={i}>{c}</li>)}
                              </ul>
                            </div>
                          ) : null}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </section>
              ) : (
                <section>
                  <div className="text-xs text-gray-500 italic bg-gray-50 border rounded p-3">
                    Denne studieplanen er ikke delt opp i moduler. Læringsutbyttene over gjelder
                    hele studieplanen.
                  </div>
                </section>
              )}

              {/* Undervisnings- og vurderingsformer */}
              {(study_plan.teaching_learning_methods || study_plan.assessment_criteria) && (
                <div className="grid sm:grid-cols-2 gap-4">
                  {study_plan.teaching_learning_methods && (
                    <section>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Undervisnings- og læringsformer</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {study_plan.teaching_learning_methods}
                      </p>
                    </section>
                  )}
                  {study_plan.assessment_criteria && (
                    <section>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Vurderingskriterier</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{study_plan.assessment_criteria}</p>
                    </section>
                  )}
                </div>
              )}
            </div>

            <div className="border-t px-6 py-3 flex justify-end bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowFullModal(false)}
                className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded"
              >
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
